import { useEffect, useMemo, useState } from 'react'
import { getTokensByChain, type Token } from '../constants/tokens'

interface TokenListJson {
  tokens?: Array<{
    chainId: number
    address: string
    name: string
    symbol: string
    decimals: number
    logoURI?: string
  }>
}

function parseTokenListUrls(): string[] {
  const raw = import.meta.env.VITE_TOKEN_LIST_URLS ?? ''
  return raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
}

function toToken(t: NonNullable<TokenListJson['tokens']>[number]): Token {
  return {
    chainId: t.chainId,
    address: t.address,
    name: t.name,
    symbol: t.symbol,
    decimals: t.decimals,
    logoURI: t.logoURI,
  }
}

function mergeLists(base: Token[], remote: Token[]): Token[] {
  const map = new Map<string, Token>()
  for (const t of base) {
    map.set(t.address.toLowerCase(), t)
  }
  for (const t of remote) {
    const k = t.address.toLowerCase()
    if (!map.has(k)) map.set(k, t)
  }
  return Array.from(map.values())
}

export interface UseMergedTokenListResult {
  tokens: Token[]
  loading: boolean
  error: string | null
}

export function useMergedTokenList(chainId: number | null): UseMergedTokenListResult {
  const base = useMemo(() => (chainId != null ? getTokensByChain(chainId) : []), [chainId])
  const urls = useMemo(() => parseTokenListUrls(), [])

  const [remote, setRemote] = useState<Token[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (chainId == null || urls.length === 0) {
      setRemote([])
      setError(null)
      setLoading(false)
      return
    }

    const ac = new AbortController()
    setLoading(true)
    setError(null)

    ;(async () => {
      const collected: Token[] = []
      for (const url of urls) {
        try {
          const res = await fetch(url, { signal: ac.signal })
          if (!res.ok) throw new Error(String(res.status))
          const json = (await res.json()) as TokenListJson
          const list = json.tokens ?? []
          for (const t of list) {
            if (t.chainId !== chainId) continue
            collected.push(toToken(t))
          }
        } catch (e) {
          if ((e as Error).name === 'AbortError') return
          console.warn('[useMergedTokenList]', url, e)
        }
      }
      if (!ac.signal.aborted) {
        setRemote(collected)
        setLoading(false)
      }
    })().catch(e => {
      if (!ac.signal.aborted) {
        setError(e instanceof Error ? e.message : 'Token list fetch failed')
        setLoading(false)
      }
    })

    return () => ac.abort()
  }, [chainId, urls])

  const tokens = useMemo(() => mergeLists(base, remote), [base, remote])

  return { tokens, loading: urls.length > 0 && loading, error }
}
